<template>
  <div
    class="c-header"
    :class="{ 'c-header_mobile': isMobile, 'c-header_body-scrolled': scrollPosition > 0 }"
  >
    <RouterLink
      class="c-header__logo"
      to="/"
    >
      <img
        src="@/assets/logo-new.png"
        alt="Shoptronic"
      />
    </RouterLink>

    <div
      v-if="!isMobile"
      class="c-header__links"
    >
      <RouterLink
        v-for="link in links"
        :key="link.id"
        class="c-header-link"
        :class="{ 'c-header-link_active': isHomePage && activeSection === link.id }"
        :to="getLinkPath(link.id)"
        @click.prevent="isHomePage ? scrollToSection(link.id) : undefined"
      >
        {{ link.title }}
      </RouterLink>
    </div>

    <CHeaderMobileMenu
      v-if="isMobile"
      class="c-header__mobile-menu"
    />
  </div>
</template>

<script setup>
  import { computed } from 'vue';
  import { useRoute } from 'vue-router';
  import { useSectionTracking } from '@/composables/useSectionTracking';
  import { useClientSize } from '@/composables/useClientSize';

  import CHeaderMobileMenu from './inner/CHeaderMobileMenu.vue';
  import { useBodyScroll } from '@/composables/useBodyScroll';

  const route = useRoute();
  const { links, activeSection, scrollToSection, getLinkPath } = useSectionTracking();
  const { isMobile } = useClientSize();
  const { scrollPosition } = useBodyScroll();

  const isHomePage = computed(() => route.path === '/');
</script>

<style>
  .c-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 22px 54px 24px;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 10;
    background: #ffffff;
    border-bottom: 1px solid transparent;

    &_body-scrolled {
      border-bottom: 1px solid #e5e5e5;
    }

    &_mobile {
      align-items: center;
      height: 47px;
      padding: 22px 24px 14px;
    }

    &__logo {
      img {
        width: 240px;
      }
    }

    &__links {
      display: flex;
      gap: 63px;
      align-items: center;
    }

    &-link {
      font-size: 24px;
      padding: 0 0 7px 0;
      font-weight: 500;
      color: #000000;
      position: relative;
      cursor: pointer;
      box-sizing: border-box;
      display: flex;
      align-items: flex-start;
      transition: all 0.3s ease;
      text-decoration: none;
      display: block;

      &:after {
        content: '';
        display: block;
        width: 100%;
        height: 5px;
        position: absolute;
        bottom: 0;
        top: calc(100% - 5px);
        left: 0;
        right: 0;
        background: var(--g-color-orange);
        transition: all 0.3s ease;
        background-size: 200% 200%;
      }

      &:hover:after {
        animation: gradient-rotation 3s infinite linear;
      }

      &:hover:after,
      &_active:after {
        opacity: 1;
      }

      &_active {
        color: #ffffff;

        &:after {
          content: '';
          display: block;
          width: calc(100% + 44px);
          height: 100%;
          position: absolute;
          bottom: 0;
          top: -3px;
          left: -22px;
          right: -22px;
          z-index: -1;
          background: var(--g-color-orange);
          transition: all 0.3s ease;
          border-radius: 37.5px;
        }
      }
    }

    &__mobile-menu {
      margin-bottom: 4px;
    }
  }

  @keyframes gradient-rotation {
    0% {
      background-position: 0% 50%;
    }

    25% {
      background-position: 50% 100%;
    }

    50% {
      background-position: 100% 50%;
    }

    75% {
      background-position: 50% 0%;
    }

    100% {
      background-position: 0% 50%;
    }
  }

  @media (--mobile) {
    .c-header {
    }
  }

  @media (--desktop-small) {
    .c-header__links {
      margin-bottom: 12px;
    }

    .c-header-link {
      font-size: 18px;
    }
  }

  @media (--tiny-mobile) {
    .c-header__logo img {
      width: 180px;
    }
  }
</style>
